/**
 * Client-side MP4 parser for building upload chunk plans.
 *
 * Handles moov-atom relocation for non-faststart files WITHOUT loading the
 * entire file into memory. Uses the File/Blob slice API so only the atoms
 * that need inspection are ever read into RAM.
 */

/** Size of each upload chunk in bytes (10 MB). */
export const CHUNK_SIZE = 10 * 1024 * 1024;

/** Top-level atom descriptor returned by {@link scanAtoms}. */
export interface Atom {
  type: string;
  offset: number;
  size: number;
}

/**
 * Describes a single chunk within a {@link ChunkPlan}.
 *
 * - `source: 'file'` — the chunk comes from a byte range of the original file.
 * - `source: 'buffer'` — the chunk comes from an in-memory ArrayBuffer
 *   (e.g. the patched moov atom).
 */
export interface ChunkDescriptor {
  index: number;
  source: 'file' | 'buffer';
  fileOffset?: number;
  fileSize?: number;
  buffer?: ArrayBuffer;
}

/**
 * The result of {@link buildChunkPlan}: a list of chunks that, when
 * concatenated, produce a valid faststart MP4.
 */
export interface ChunkPlan {
  chunks: ChunkDescriptor[];
  totalSize: number;
  isRelocated: boolean;
  originalFilename: string;
}

/**
 * Container atom types whose children begin at byte offset 8 from the atom
 * start (i.e. immediately after the 4-byte size + 4-byte type header).
 */
const CONTAINER_ATOMS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl', 'udta', 'edts']);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Read a byte range from a File into an ArrayBuffer.
 *
 * This avoids loading the entire file; only the requested slice is read.
 */
async function readSlice(file: File, offset: number, length: number): Promise<ArrayBuffer> {
  const blob = file.slice(offset, offset + length);
  return blob.arrayBuffer();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Scan the top-level atoms of an MP4 file.
 *
 * Reads only the 8-byte (or 16-byte for extended-size) header of each atom,
 * so memory usage is negligible regardless of file size. Stops early once
 * both `moov` and `mdat` have been found.
 *
 * @param file - The MP4 File to scan.
 * @returns An array of top-level atom descriptors.
 */
export async function scanAtoms(file: File): Promise<Atom[]> {
  const atoms: Atom[] = [];
  let offset = 0;
  let hasMoviAtom = false;
  let hasMdatAtom = false;

  while (offset < file.size) {
    // Need at least 8 bytes for the basic header.
    if (offset + 8 > file.size) break;

    const headerBuf = await readSlice(file, offset, 16); // up to 16 for extended
    const view = new DataView(headerBuf);

    let size = view.getUint32(0); // big-endian by default
    const type = String.fromCharCode(
      view.getUint8(4),
      view.getUint8(5),
      view.getUint8(6),
      view.getUint8(7),
    );

    if (size === 1) {
      // Extended size: next 8 bytes hold the real size (64-bit big-endian).
      // JavaScript numbers can safely represent integers up to 2^53, which is
      // far larger than any realistic MP4 atom.
      const hi = view.getUint32(8);
      const lo = view.getUint32(12);
      size = hi * 0x1_0000_0000 + lo;
    } else if (size === 0) {
      // Atom extends to the end of the file.
      size = file.size - offset;
    }

    atoms.push({ type, offset, size });

    if (type === 'moov') hasMoviAtom = true;
    if (type === 'mdat') hasMdatAtom = true;
    if (hasMoviAtom && hasMdatAtom) break;

    offset += size;
  }

  return atoms;
}

/**
 * Patch chunk-offset tables (`stco` / `co64`) inside a moov atom buffer.
 *
 * When relocating the moov atom before the mdat atom, every absolute file
 * offset stored inside `stco` and `co64` boxes must be shifted by the size
 * of the moov atom (since it is being inserted before the media data).
 *
 * The function mutates the buffer in place.
 *
 * @param moovBuffer - The moov atom contents (including the 8-byte header).
 * @param shift      - The number of bytes to add to each offset entry.
 */
export function patchChunkOffsets(moovBuffer: ArrayBuffer, shift: number): void {
  const view = new DataView(moovBuffer);

  /**
   * Recursively walk atoms inside a container.
   *
   * Container atoms (moov, trak, mdia, ...) store their children starting at
   * offset 8 from the atom's own start.
   */
  function walk(start: number, end: number): void {
    let pos = start;

    while (pos + 8 <= end) {
      const atomSize = view.getUint32(pos);
      const atomType = String.fromCharCode(
        view.getUint8(pos + 4),
        view.getUint8(pos + 5),
        view.getUint8(pos + 6),
        view.getUint8(pos + 7),
      );

      // Guard against zero-size or corrupt atoms to avoid infinite loops.
      if (atomSize < 8) break;

      const atomEnd = pos + atomSize;

      if (atomType === 'stco') {
        // stco: 8 header + 1 version + 3 flags + 4 entry_count + N*4 offsets
        const entryCount = view.getUint32(pos + 12);
        for (let i = 0; i < entryCount; i++) {
          const entryOffset = pos + 16 + i * 4;
          const oldValue = view.getUint32(entryOffset);
          view.setUint32(entryOffset, oldValue + shift);
        }
      } else if (atomType === 'co64') {
        // co64: 8 header + 1 version + 3 flags + 4 entry_count + N*8 offsets
        const entryCount = view.getUint32(pos + 12);
        for (let i = 0; i < entryCount; i++) {
          const entryOffset = pos + 16 + i * 8;
          const hi = view.getUint32(entryOffset);
          const lo = view.getUint32(entryOffset + 4);
          const oldValue = hi * 0x1_0000_0000 + lo;
          const newValue = oldValue + shift;
          view.setUint32(entryOffset, Math.floor(newValue / 0x1_0000_0000));
          view.setUint32(entryOffset + 4, newValue >>> 0);
        }
      } else if (CONTAINER_ATOMS.has(atomType)) {
        // Recurse into container atoms — children start at offset 8.
        walk(pos + 8, atomEnd);
      }

      pos = atomEnd;
    }
  }

  // moov itself is a container; children start at byte 8.
  walk(8, moovBuffer.byteLength);
}

/**
 * Build an upload chunk plan for an MP4 file.
 *
 * If the file is already faststart (moov before mdat), the plan is simply a
 * sequence of 10 MB slices covering the whole file.
 *
 * If the moov atom comes after mdat, the moov is read into memory, its
 * chunk-offset tables are patched, and the plan reorders the atoms so the
 * output is: ftyp + patched moov + mdat + trailing atoms.
 *
 * @param file - The MP4 File to process.
 * @returns A ChunkPlan describing the upload chunks.
 */
export async function buildChunkPlan(file: File): Promise<ChunkPlan> {
  const atoms = await scanAtoms(file);

  const ftyp = atoms.find((a) => a.type === 'ftyp');
  const moov = atoms.find((a) => a.type === 'moov');
  const mdat = atoms.find((a) => a.type === 'mdat');

  if (!moov || !mdat) {
    throw new Error('MP4 file is missing required moov or mdat atoms');
  }

  // --- Faststart: moov is already before mdat ---
  if (moov.offset < mdat.offset) {
    const chunks: ChunkDescriptor[] = [];
    let idx = 0;
    for (let off = 0; off < file.size; off += CHUNK_SIZE) {
      const remaining = file.size - off;
      const chunkSize = Math.min(CHUNK_SIZE, remaining);
      chunks.push({
        index: idx++,
        source: 'file',
        fileOffset: off,
        fileSize: chunkSize,
      });
    }
    return {
      chunks,
      totalSize: file.size,
      isRelocated: false,
      originalFilename: file.name,
    };
  }

  // --- Non-faststart: relocate moov before mdat ---

  const ftypSize = ftyp ? ftyp.size : 0;
  const ftypOffset = ftyp ? ftyp.offset : 0;

  // Read the moov atom into memory and patch its chunk offsets.
  // Shift = difference between new mdat position and original mdat position.
  // New layout: ftyp(0, F) + moov(F, V) + mdat(F+V, M)
  // Original stco entries reference absolute offsets into the original mdat.
  // New offset = original_offset + (new_mdat_start - original_mdat_start)
  const moovArrayBuffer = await readSlice(file, moov.offset, moov.size);
  const shift = ftypSize + moov.size - mdat.offset;
  patchChunkOffsets(moovArrayBuffer, shift);

  const chunks: ChunkDescriptor[] = [];
  let idx = 0;

  if (ftyp) {
    const ftypBuf = await readSlice(file, ftypOffset, ftypSize);
    const combined = new Uint8Array(ftypSize + moov.size);
    combined.set(new Uint8Array(ftypBuf), 0);
    combined.set(new Uint8Array(moovArrayBuffer), ftypSize);
    chunks.push({
      index: idx++,
      source: 'buffer',
      buffer: combined.buffer,
    });
  } else {
    // No ftyp — just send the moov.
    chunks.push({
      index: idx++,
      source: 'buffer',
      buffer: moovArrayBuffer,
    });
  }

  // mdat in 10 MB slices (from the original file).
  for (let off = 0; off < mdat.size; off += CHUNK_SIZE) {
    const remaining = mdat.size - off;
    const chunkSize = Math.min(CHUNK_SIZE, remaining);
    chunks.push({
      index: idx++,
      source: 'file',
      fileOffset: mdat.offset + off,
      fileSize: chunkSize,
    });
  }

  // Trailing atoms after moov (anything that isn't ftyp, moov, or mdat and
  // comes after the moov atom).
  for (const atom of atoms) {
    if (
      atom.type !== 'ftyp' &&
      atom.type !== 'moov' &&
      atom.type !== 'mdat' &&
      atom.offset > moov.offset
    ) {
      chunks.push({
        index: idx++,
        source: 'file',
        fileOffset: atom.offset,
        fileSize: atom.size,
      });
    }
  }

  // Compute total output size.
  let totalSize = ftypSize + moov.size + mdat.size;
  for (const atom of atoms) {
    if (
      atom.type !== 'ftyp' &&
      atom.type !== 'moov' &&
      atom.type !== 'mdat' &&
      atom.offset > moov.offset
    ) {
      totalSize += atom.size;
    }
  }

  return {
    chunks,
    totalSize,
    isRelocated: true,
    originalFilename: file.name,
  };
}
